import type {
  ActivityIngredientAdapter,
  HeartedHotel,
  HeartedRoom,
  HotelIngredientAdapter,
  ItinerarySearchCriteria,
  OwnStayIngredientAdapter,
  RecipeMetaData,
  TransportIngredientAdapter,
} from '@libs/trip-planner-interfaces';
import {
  TransportVehicleType,
  GraphQL,
  IngredientType,
  AvailabilityOrder,
} from '@libs/trip-planner-interfaces';
import { assertValue, exhaustiveGuard, isDefined } from '@libs/utils';
import {
  getSmartSearchSerializedFeatureToggles,
  isFeatureToggleActive,
} from '@libs/feature-toggle';

import {
  getMarketOrigin,
  normalizeDateOnlyString,
  setSessionId,
} from '../../utils';

import { getInputPaxGroupQL } from './inputPaxGroupQL';

export function getRecipeQL(
  criteria: ItinerarySearchCriteria
): GraphQL.RecipeQL {
  setSessionId(criteria);

  const ingredients = criteria.userCriteria.ingredients;
  assertValue(ingredients, 'search-service-smart-search > utils > getRecipeQL');

  return {
    pax: getInputPaxGroupQL(criteria.userCriteria.pax),
    ingredients: ingredients.map(getIngredientQL),
    market: getMarketOrigin(),
    startDateOnly: normalizeDateOnlyString(
      criteria.userCriteria.startSearchDate
    ),
    endDateOnly: normalizeDateOnlyString(criteria.userCriteria.endSearchDate),
    affiliateId: criteria.businessCriteria.affiliateId,
    featureToggles: getSmartSearchSerializedFeatureToggles(),
    skipCache: isFeatureToggleActive('skip-smart-search-cache'),
    returnEmptyIngredientResults: true,
    sessionId: criteria.userCriteria.sessionId,
    metadata: getMetadataRequestQL(criteria.businessCriteria.metaData),
    searchForPackageRates: criteria.businessCriteria.searchForPackageRates,
  };
}

function getMetadataRequestQL(
  metaData?: RecipeMetaData
): GraphQL.MetadataRequestQL {
  return {
    sourceType: GraphQL.InputSourceTypeQL.Planner,
    generatedFromAI: metaData?.generatedFromAI,
  };
}

const NUMBER_OF_HOTEL_SUGGESTIONS = 15;

function getIngredientQL(
  ingredient:
    | OwnStayIngredientAdapter
    | HotelIngredientAdapter
    | TransportIngredientAdapter
    | ActivityIngredientAdapter
): GraphQL.IngredientQL {
  switch (ingredient.ingredientType) {
    case IngredientType.OwnStay: {
      const { destination } = ingredient.constraint;
      return {
        destination,
        ingredientType: GraphQL.InputIngredientTypeQL.OwnStay,
        numberOfNights: ingredient.numberOfNights,
        subIngredients: ingredient.subIngredients.map(getIngredientQL),
      };
    }

    case IngredientType.Hotel: {
      return {
        ...ingredient.constraint,
        boardBasis: ingredient.constraint.boardBasis,
        selectedHotelTypes: ingredient.constraint.selectedHotelTypes,
        ingredientType: GraphQL.InputIngredientTypeQL.Hotel,
        numberOfNights: ingredient.numberOfNights,
        numberOfSuggestions: NUMBER_OF_HOTEL_SUGGESTIONS,
        heartedHotels: ingredient.heartedHotels.map(getHeartedHotelQL),
        subIngredients: ingredient.subIngredients.map(getIngredientQL),
        suggestedBy: getInputAvailibityOrderQL(ingredient.suggestedBy),
        includeAlternativeResults: true,
      };
    }

    case IngredientType.Activity: {
      return {
        ingredientType: GraphQL.InputIngredientTypeQL.Ticket,
        code: ingredient.code,
        offset: ingredient.offset,
        tickets: ingredient.tickets,
        includeAlternativeResults: true,
      };
    }

    case IngredientType.Flight: {
      return {
        transportTypes: [GraphQL.InputTransportTypeQL.Flight],
        ingredientType: GraphQL.InputIngredientTypeQL.Transport,
        fromIata: ingredient.fromPort,
        toIata: ingredient.toPort,
        flightNumbers: ingredient.transportIds,
        maxNumberOfStops: ingredient.maxNumberOfStops,
        departureTimeFilters: ingredient.departureTimeFilters,
        arrivalTimeFilters: ingredient.arrivalTimeFilters,
        maxDurationInHours: ingredient.maxDurationInHours,
        priceFilters: ingredient.priceFilters,
        carriers: ingredient.carrierFilters,
        suggestedBy: getInputAvailibityOrderQL(ingredient.suggestedBy),
        includeAlternativeResults: true,
      };
    }

    case IngredientType.Ferry: {
      return {
        transportTypes: [GraphQL.InputTransportTypeQL.Ferry],
        ingredientType: GraphQL.InputIngredientTypeQL.Transport,
        fromIata: ingredient.fromPort,
        toIata: ingredient.toPort,
        flightNumbers: ingredient.transportIds,
        departureTimeFilters: ingredient.departureTimeFilters,
        arrivalTimeFilters: ingredient.arrivalTimeFilters,
        maxDurationInHours: ingredient.maxDurationInHours,
        priceFilters: ingredient.priceFilters,
        carriers: ingredient.carrierFilters,
        vehicles: ingredient.vehicles.map((vehicle) =>
          getInputTransportVehicleTypeQL(vehicle)
        ),
        suggestedBy: getInputAvailibityOrderQL(ingredient.suggestedBy),
      };
    }

    default:
      exhaustiveGuard(ingredient, 'Ingredient adapter not handled');
  }
}

function getHeartedHotelQL(heartedHotel: HeartedHotel): GraphQL.HeartedHotelQL {
  const heartedHotelQL: GraphQL.HeartedHotelQL = {
    code: heartedHotel.code,
    isRefundable: heartedHotel.isRefundable,
    roomCodes: heartedHotel.roomCodes,
    rooms: heartedHotel.rooms?.map(getHeartedRoomQL),
  };

  if (heartedHotelQL.rooms && heartedHotelQL.rooms.length) {
    delete heartedHotelQL.roomCodes;
  }

  return heartedHotelQL;
}

function getHeartedRoomQL(heartedRoom: HeartedRoom): GraphQL.HeartedRoomQL {
  const heartedRoomQL: GraphQL.HeartedRoomQL = {
    boardBasis: heartedRoom.boardBasis,
    code: heartedRoom.code,
    roomTypeId: heartedRoom.roomTypeId,
  };

  if (isDefined(heartedRoomQL.roomTypeId)) {
    delete heartedRoomQL.code;
  }

  return heartedRoomQL;
}

function getInputAvailibityOrderQL(
  availabilityOrder: AvailabilityOrder = AvailabilityOrder.None
): GraphQL.InputAvailibityOrderQL {
  switch (availabilityOrder) {
    case AvailabilityOrder.None:
      return GraphQL.InputAvailibityOrderQL.None;

    case AvailabilityOrder.Cheapest:
      return GraphQL.InputAvailibityOrderQL.Cheapest;

    case AvailabilityOrder.Popular:
      return GraphQL.InputAvailibityOrderQL.Popular;

    default:
      exhaustiveGuard(availabilityOrder, 'Unhandled availability order');
  }
}

function getInputTransportVehicleTypeQL(
  vehicle: TransportVehicleType
): GraphQL.InputTransportVehicleTypeQL {
  switch (vehicle) {
    case TransportVehicleType.None:
      return GraphQL.InputTransportVehicleTypeQL.None;

    case TransportVehicleType.Bicycle:
      return GraphQL.InputTransportVehicleTypeQL.Bicycle;

    case TransportVehicleType.MiniBus4mHigh6mLong:
      return GraphQL.InputTransportVehicleTypeQL.MiniBus_4MHigh_6MLong;

    case TransportVehicleType.MiniBus8mLong:
      return GraphQL.InputTransportVehicleTypeQL.MiniBus_8MLong;

    case TransportVehicleType.MotorBike:
      return GraphQL.InputTransportVehicleTypeQL.MotorBike;

    case TransportVehicleType.MultiPurposeVehicle:
      return GraphQL.InputTransportVehicleTypeQL.MultiPurposeVehicle;

    case TransportVehicleType.Vehicle4m70cmLong:
      return GraphQL.InputTransportVehicleTypeQL.Vehicle_4M_70CmLong;

    case TransportVehicleType.Vehicle6mLong:
      return GraphQL.InputTransportVehicleTypeQL.Vehicle_6MLong;

    default:
      exhaustiveGuard(vehicle, 'Unhandled vehicle type');
  }
}
